import { Component, h, Prop, Element } from "@stencil/core";
import { Controller, Scene } from "scrollmagic";

import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";

interface ScrollOnPlaceCard {
  preTitle?: string;
  title: string;
  description?: string;
  linkHref?: string;
  linkLabel?: string; // optional custom label
  imageSrc: string; // desktop image (or generic)
  imageMobileSrc?: string; // mobile override
  // Advanced formats
  lottieSrc?: string; // .json / .lot path
  brightcoveVideoId?: string; // placeholder support
  brightcoveAccountId?: string; // optional
}

@Component({
  tag: "scs-scroll-on-place",
  styleUrls: ["scroll-on-place.scss"],
  shadow: true,
})
export class ScrollOnPlace {
  @Element() hostEl!: HTMLElement;

  /** JSON encoded array (max 4) of cards */
  @Prop() cards: string = "[]";
  /** Image side on desktop */
  @Prop() imagePosition: "left" | "right" = "left";
  /** Enable tracking */
  @Prop() trackingEnabled = false;
  /** JSON with tracking attributes */
  @Prop() trackingAttributes = "{}";

  private cardsData: ScrollOnPlaceCard[] = [];
  private controller: Controller | undefined;
  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;
  private readonly parsedTrackingAttributes: { [key: string]: string } = {};
  private readonly trackByEvent: (event: Event) => void;

  constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
    if (this.trackingEnabled) {
      try {
        this.parsedTrackingAttributes = JSON.parse(this.trackingAttributes);
      } catch (error) {
        console.error("Error parsing tracking attributes:", error);
      }
    }
  }

  componentWillLoad() {
    this.parseCards();
  }

  createScene = (
    controller: Controller,
    currentElement: Element,
    pin: Element,
    duration: number,
    offset: number,
    triggerHook: number,
    isFirstElement = false,
    isLastElement = false,
    previousElement: Element | null = null,
  ) => {
    const scene = new Scene({
      triggerElement: currentElement,
      duration: duration,
      offset: offset,
      triggerHook: triggerHook,
    }).setPin(pin);

    // Fade logic: element becomes visible on enter, starts fading near end of scene.
    // const fadeStart = 0.75; // progress threshold at which fade-out begins
    const fadeStart = 1; // progress threshold at which fade-out begins
    if (this.isDesktop()) {
      scene.on("enter", () => {
        (pin as HTMLElement).classList.add("visible");
        (currentElement as HTMLElement).classList.add("active");
        (pin as HTMLElement).classList.remove("fade-out");
      });
      scene.on("progress", (event: any) => {
        // Only trigger fade-out while scrolling forward; remove during reverse so it stays visible.
        if (event.scrollDirection === "FORWARD") {
          if (event.progress >= fadeStart) {
            (pin as HTMLElement).classList.add("fade-out");
          } else {
            (pin as HTMLElement).classList.remove("fade-out");
          }
        } else if (event.scrollDirection === "REVERSE") {
          (pin as HTMLElement).classList.remove("fade-out");
        }
      });
      scene.on("leave", (event: any) => {
        // If we are leaving because we've scrolled past the end going forward, hide.
        // If we are leaving in reverse (going back to BEFORE), keep it visible so the first image stays shown at top.
        if (event.scrollDirection === "FORWARD") {
          if (isLastElement) {
            (currentElement as HTMLElement).classList.add("active");
            (pin as HTMLElement).classList.add("visible");
          } else {
            (currentElement as HTMLElement).classList.remove("active");
            (pin as HTMLElement).classList.remove("visible");
          }
        } else if (event.scrollDirection === "REVERSE") {
          (currentElement as HTMLElement).classList.remove("active");
          (pin as HTMLElement).classList.remove("visible");
          if (isFirstElement) {
            (pin as HTMLElement).classList.add("visible");
            (currentElement as HTMLElement).classList.add("active");
          } else {
            (pin as HTMLElement).classList.remove("visible");
            (currentElement as HTMLElement).classList.remove("active");
          }
        }
        (pin as HTMLElement).classList.remove("fade-out");
      });
    } else {
      // Mobile
      scene.on("enter", () => {
        (pin as HTMLElement).classList.add("visible");
        (currentElement as HTMLElement).classList.add("active");
        (pin as HTMLElement).classList.remove("fade-out");
        previousElement?.classList?.remove("visible");
        previousElement?.classList?.add("fade-out");
      });
      scene.on("progress", (event: any) => {
        // Only trigger fade-out while scrolling forward; remove during reverse so it stays visible.
        if (event.scrollDirection === "FORWARD") {
          if (event.progress >= fadeStart) {
            (pin as HTMLElement).classList.add("fade-out");
          } else {
            (pin as HTMLElement).classList.remove("fade-out");
          }
        } else if (event.scrollDirection === "REVERSE") {
          (pin as HTMLElement).classList.remove("fade-out");
        }
      });
      scene.on("leave", (event: any) => {
        // If we are leaving because we've scrolled past the end going forward, hide.
        // If we are leaving in reverse (going back to BEFORE), keep it visible so the first image stays shown at top.
        if (event.scrollDirection === "FORWARD") {
          if (isLastElement) {
            (currentElement as HTMLElement).classList.add("active");
            (pin as HTMLElement).classList.add("visible");
          } else {
            (currentElement as HTMLElement).classList.remove("active");
            (pin as HTMLElement).classList.remove("visible");
          }
        } else if (event.scrollDirection === "REVERSE") {
          if (isFirstElement) {
            (currentElement as HTMLElement).classList.add("active");
            (pin as HTMLElement).classList.add("visible");
          } else {
            (pin as HTMLElement).classList.remove("visible");
            (currentElement as HTMLElement).classList.remove("active");
          }
        }
        (pin as HTMLElement).classList.remove("fade-out");
      });
    }

    scene.addTo(controller);
    return scene;
  };

  setImageWidth(isDesktop: boolean) {
    const imgWidth =
      this.hostEl.shadowRoot?.querySelector(".cards")?.clientWidth;
    if (!imgWidth) {
      console.error("Couldn't check image width for scroll-on-place element.");
      return;
    }
    const imgs = [
      ...(this.hostEl.shadowRoot?.querySelectorAll(".card-image-container") ??
        []),
    ];
    imgs.forEach(img => {
      (img as HTMLElement).style.width = isDesktop
        ? imgWidth / 2 + "px"
        : imgWidth + "px";
    });
  }

  componentDidLoad() {
    this.controller = new Controller();
    const sectionElements = [
      ...(this.hostEl.shadowRoot?.querySelectorAll(".trigger") ?? []),
    ];

    for (let i = 0; i < sectionElements.length; i++) {
      const isFirstElement = i === 0;
      const isLastElement = i === sectionElements.length - 1;
      const currentElement = sectionElements[i];
      const pin = currentElement?.querySelector(".card-image-container");
      this.setImageWidth(this.isDesktop());

      if (currentElement && pin) {
        if (this.isDesktop()) {
          const offset = -100;
          const duration = isLastElement
            ? 300
            : (currentElement as any).offsetHeight;
          const triggerHook = 0;

          this.createScene(
            this.controller,
            currentElement,
            pin,
            duration,
            offset,
            triggerHook,
            isFirstElement,
            isLastElement,
            sectionElements[i - 1] ?? null,
          );
        } else {
          // Mobile
          const offset = -50;
          const duration = (currentElement as any).clientHeight;
          const triggerHook = 0;
          this.createScene(
            this.controller,
            currentElement,
            pin,
            duration,
            offset,
            triggerHook,
            isFirstElement,
            isLastElement,
          );
        }
      }
    }

    // Ensure first image is visible immediately (in case progress events haven't fired yet)
    const firstPin = sectionElements[0]?.querySelector(".card-image-container");
    const firstElement = sectionElements[0];
    if (firstPin) {
      firstPin.classList.add("visible");
      (firstElement as HTMLElement).classList.add("active");
    }
  }

  disconnectedCallback() {
    this.controller?.destroy(true);
  }

  private parseCards() {
    try {
      const arr = JSON.parse(this.cards || "[]");
      if (Array.isArray(arr)) {
        this.cardsData = arr
          .filter(
            c =>
              c &&
              c.title &&
              (c.imageSrc || c.lottieSrc || c.brightcoveVideoId),
          )
          .slice(0, 4);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error parsing cards data:", e);
      this.cardsData = [];
    }
  }

  private isDesktop() {
    return window.innerWidth >= 768; // align with project breakpoint usage
  }

  private getImageForCard(card: ScrollOnPlaceCard) {
    if (this.isDesktop()) {
      return card.imageSrc;
    }
    return card.imageMobileSrc || card.imageSrc;
  }

  public render() {
    if (!this.cardsData.length) {
      return <div class="sop-wrapper sop-empty">No cards configured</div>;
    }
    return (
      <ul class="cards">
        {this.cardsData.map((card, i) => (
          <li
            key={i}
            class={`card-container trigger`}
            id={`scrollmagic-trigger-${i}`}
            style={
              this.imagePosition === "right" && this.isDesktop()
                ? { flexDirection: "row-reverse" }
                : {}
            }
          >
            <div class="card-image-container">
              <img
                class="card-image"
                src={this.getImageForCard(card)}
                id={`${i}`}
                alt={card.title}
              />
            </div>
            <div class="card-text-content">
              {card.preTitle && <h3 class="h4">{card.preTitle}</h3>}
              <h2 class="d2 card-title">{card.title}</h2>
              {card.description && (
                <p class="card-description">{card.description}</p>
              )}
              {card.linkHref && card.linkLabel && (
                <a
                  href={card.linkHref}
                  class="link link--standalone"
                  data-track-action={this.trackingEnabled && "link"}
                  {...this.parsedTrackingAttributes}
                  onClick={(ev: Event) => {
                    if (this.trackingEnabled) {
                      this.trackByEvent(ev);
                    }
                  }}
                >
                  {card.linkLabel}
                </a>
              )}
            </div>
          </li>
        ))}
        <li class="trailing-spacer" aria-hidden="true"></li>
      </ul>
    );
  }
}
